import { z } from 'zod';

export const quoteJobTypes = [
  'FOUNDATION',
  'DRIVEWAY',
  'PATIO',
  'FENCING',
  'REPAIR',
  'OTHER'
] as const;
export const QuoteJobTypeSchema = z.enum(quoteJobTypes);
export type QuoteJobType = z.infer<typeof QuoteJobTypeSchema>;

export const rateUnits = ['M2', 'M3', 'LM', 'EACH', 'HOUR', 'FIXED'] as const;
export const RateUnitSchema = z.enum(rateUnits);
export type RateUnit = z.infer<typeof RateUnitSchema>;

export const rateUnitLabels: Record<RateUnit, string> = {
  M2: 'm²',
  M3: 'm³',
  LM: 'lm',
  EACH: 'each',
  HOUR: 'hr',
  FIXED: 'fixed'
};

/** Where a quote request came from. Website intake is the default; the rest
 * are logged manually from the dashboard. */
export const quoteRequestSources = ['website', 'email', 'phone', 'in_person', 'other'] as const;
export const QuoteRequestSourceSchema = z.enum(quoteRequestSources);
export type QuoteRequestSource = z.infer<typeof QuoteRequestSourceSchema>;

export const quoteRequestSourceLabels: Record<QuoteRequestSource, string> = {
  website: 'Website',
  email: 'Email',
  phone: 'Phone',
  in_person: 'In person',
  other: 'Other'
};

/** Best-effort measurements captured at intake. All optional. */
export const QuoteMeasurementsSchema = z.object({
  areaM2: z.coerce.number().min(0).max(100_000).optional(),
  perimeterLm: z.coerce.number().min(0).max(100_000).optional()
});
export type QuoteMeasurements = z.infer<typeof QuoteMeasurementsSchema>;

/** Public quote-request intake (website form). */
export const QuoteRequestInputSchema = z.object({
  name: z.string().min(1, 'Name is required').max(80).trim(),
  email: z.string().email('Invalid email address'),
  phone: z.string().max(30).optional().default(''),
  company: z.string().max(120).optional().default(''),
  siteAddress: z.string().max(200).optional().default(''),
  city: z.string().max(80).optional().default(''),
  jobType: QuoteJobTypeSchema.default('OTHER'),
  description: z
    .string()
    .min(5, 'Tell us a little about the job')
    .max(4000, 'Description is too long'),
  targetDate: z.string().max(60).optional().default(''),
  measurements: QuoteMeasurementsSchema.optional(),
  fileKeys: z
    .array(
      z.object({
        key: z.string().min(1).max(300),
        name: z.string().min(1).max(200),
        size: z.number().int().positive(),
        contentType: z.string().min(1).max(100)
      })
    )
    .max(10, 'Too many files')
    .optional()
    .default([])
});
export type QuoteRequestInput = z.infer<typeof QuoteRequestInputSchema>;

/**
 * Dashboard-logged quote request (came in by email, phone or in person).
 * Only the name is required — an in-person enquiry may have no email yet,
 * it just can't be *sent* a quote until one is added.
 */
export const manualQuoteRequestSources = ['email', 'phone', 'in_person', 'other'] as const;

export const ManualQuoteRequestInputSchema = z.object({
  source: z.enum(manualQuoteRequestSources).default('email'),
  name: z.string().min(1, 'Name is required').max(80).trim(),
  email: z.string().email('Invalid email address').or(z.literal('')).default(''),
  phone: z.string().max(30).default(''),
  company: z.string().max(120).default(''),
  siteAddress: z.string().max(200).default(''),
  city: z.string().max(80).default(''),
  jobType: QuoteJobTypeSchema.default('OTHER'),
  description: z.string().max(4000).default(''),
  targetDate: z.string().max(60).default(''),
  measurements: QuoteMeasurementsSchema.optional()
});
export type ManualQuoteRequestInput = z.infer<typeof ManualQuoteRequestInputSchema>;

export const quoteRequestStatuses = ['NEW', 'REVIEWING', 'QUOTED', 'WON', 'LOST'] as const;
export const QuoteRequestStatusSchema = z.enum(quoteRequestStatuses);
export type QuoteRequestStatus = z.infer<typeof QuoteRequestStatusSchema>;

export const quoteStatuses = ['DRAFT', 'SENT', 'ACCEPTED', 'DECLINED', 'EXPIRED'] as const;
export const QuoteStatusSchema = z.enum(quoteStatuses);
export type QuoteStatus = z.infer<typeof QuoteStatusSchema>;

export const QuoteLineItemSchema = z.object({
  id: z.string().min(1),
  description: z.string().max(300).default(''),
  qty: z.coerce.number().min(0).max(1_000_000).default(0),
  unit: RateUnitSchema.default('EACH'),
  rate: z.coerce.number().min(0).max(10_000_000).default(0),
  /** Computed = round(qty * rate, 2); recomputed server-side, never trusted */
  total: z.coerce.number().default(0),
  rateItemId: z.string().optional(),
  /**
   * Rate-table cost this line was drafted from. While present, the charged
   * rate is derived = baseRate × (1 + marginPct/100); manually overriding the
   * rate clears it. Never shown to the customer.
   */
  baseRate: z.coerce.number().min(0).max(10_000_000).optional()
});
export type QuoteLineItem = z.infer<typeof QuoteLineItemSchema>;

export const QuoteDraftSchema = z.object({
  title: z.string().max(200).default(''),
  siteAddress: z.string().max(200).default(''),
  lineItems: z.array(QuoteLineItemSchema).max(100),
  gstRate: z.number().min(0).max(1).default(0.15),
  /** Markup applied to rate-table costs (baseRate lines), e.g. 15 = +15%. */
  marginPct: z.coerce.number().min(0).max(100).default(0),
  validUntil: z.string().optional(),
  notes: z.string().max(4000).default('')
});
export type QuoteDraft = z.infer<typeof QuoteDraftSchema>;
/** Pre-parse shape — fields with schema defaults are optional. */
export type QuoteDraftInput = z.input<typeof QuoteDraftSchema>;

export const RateItemInputSchema = z.object({
  name: z.string().min(1, 'Name is required').max(120).trim(),
  category: z.string().max(60).optional().default(''),
  unit: RateUnitSchema.default('EACH'),
  rate: z.coerce.number().min(0).max(10_000_000),
  isActive: z.boolean().default(true),
  sortOrder: z.number().int().default(0)
});
export type RateItemInput = z.infer<typeof RateItemInputSchema>;

/** Org-wide quoting defaults, editable on the rates settings page. */
export const QuoteSettingsInputSchema = z.object({
  defaultMarginPct: z.coerce.number().min(0).max(100)
});
export type QuoteSettingsInput = z.infer<typeof QuoteSettingsInputSchema>;

/** Upload constraints for public plan uploads. */
export const QUOTE_UPLOAD_MAX_BYTES = 25 * 1024 * 1024;
export const QUOTE_UPLOAD_CONTENT_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic'
] as const;

export const QuoteUploadRequestSchema = z.object({
  name: z.string().min(1).max(200),
  contentType: z.enum(QUOTE_UPLOAD_CONTENT_TYPES),
  size: z
    .number()
    .int()
    .positive()
    .max(QUOTE_UPLOAD_MAX_BYTES, 'File is too large (25MB max)')
});
export type QuoteUploadRequest = z.infer<typeof QuoteUploadRequestSchema>;
